import { csvParse } from "d3-dsv";

import { apiFetch } from "../../../lib/api";
import { safeParseDate } from "./utils";

export type OverviewRow = {
  id1: string;
  id2: string;
  firstMeasure: Date | null;
  lastMeasure: Date | null;
  trackingNumber: string;
  organisation: string;
  scanLocation: string;
  articleEi: string;
  expeditionCount: number;
};

export type CountriesRow = {
  country: string;
  count: number;
};

export type ArticlesRow = {
  article: string;
  count: number;
};

export type SamplePoint = {
  date: Date;
  value: number;
};

export type SensorSeries = {
  sensorId: string;
  order: number;
  samples: SamplePoint[];
};

export type ExpeditionSeries = {
  sensorId: string;
  expeditionIndex: number;
  samples: SamplePoint[];
};

export type TrackingDetails = {
  sensors: SensorSeries[];
  expeditions: ExpeditionSeries[];
  startDate: Date | null;
  endDate: Date | null;
};

type CsvRow = Record<string, string>;

const API_ENDPOINT = "/api/blulog/files";

function normalisePrefix(value?: string | null) {
  if (!value) return "";
  return value.replace(/^[/]+|\/+$/g, "");
}

function sanitiseKey(value: string) {
  return value.replace(/^[/]+/, "");
}

export function createBlulogFileUrl(prefix: string, key: string, options?: { absolute?: boolean; prefixOverride?: string }) {
  const params = new URLSearchParams();
  params.set("key", sanitiseKey(key));

  if (options?.absolute) {
    params.set("absolute", "1");
  } else {
    const base = normalisePrefix(options?.prefixOverride ?? prefix);
    if (base) {
      params.set("prefix", base);
    }
  }

  return `${API_ENDPOINT}?${params.toString()}`;
}

async function fetchCsvText(url: string): Promise<string> {
  if (!url) {
    throw new Error("Missing URL");
  }

  const response = await apiFetch(url, { cache: "no-store" });
  if (!response.ok) {
    if (response.status === 404) {
      console.info(`[blulog] Ressource introuvable (${url}).`);
      return "";
    }
    throw new Error(`Impossible de récupérer ${url}: ${response.status} ${response.statusText}`);
  }
  return response.text();
}

function ensureNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }
  return Number.NaN;
}

function normaliseOverviewRow(row: CsvRow): OverviewRow {
  const first = safeParseDate(row["Date_de_premiere_mesure"]);
  const last = safeParseDate(row["Date_de_derniere_mesure"]);

  const expeditionCountRaw = row["Nombre_dexpe"] ?? row["Nombre_d_expe"] ?? "";
  const expeditionCount = Number.isFinite(Number(expeditionCountRaw))
    ? Number(expeditionCountRaw)
    : ensureNumber(expeditionCountRaw);

  return {
    id1: row["Id1"] ?? "",
    id2: row["Id2"] ?? "",
    firstMeasure: first,
    lastMeasure: last,
    trackingNumber: row["Numeros_de_suivi"] ?? row["Numero_de_suivi"] ?? "",
    organisation: row["Organisation"] ?? "",
    scanLocation: row["Lieu_du_scan"] ?? row["Lieu_scans"] ?? "",
    articleEi: row["Article_EI"] ?? row["Article"] ?? "",
    expeditionCount: Number.isFinite(expeditionCount) ? Number(expeditionCount) : 0,
  };
}

export async function fetchOverview(url: string): Promise<OverviewRow[]> {
  if (!url) return [];
  try {
    const csvText = await fetchCsvText(url);
    if (!csvText.trim()) return [];
    const rows = csvParse(csvText) as CsvRow[];
    return rows.map(normaliseOverviewRow);
  } catch (error) {
    console.warn("[blulog] fetchOverview fallback:", error);
    return [];
  }
}

function normaliseCountryRow(row: CsvRow): CountriesRow {
  const country =
    row["unique_countries"] ?? row["country"] ?? row["Pays"] ?? row["country_name"] ?? "N/A";
  const count =
    ensureNumber(row["count_countries"] ?? row["count"] ?? row["Nombre"] ?? row["occurrence"]);
  return {
    country,
    count: Number.isFinite(count) ? Number(count) : 0,
  };
}

export async function fetchCountries(url: string): Promise<CountriesRow[]> {
  if (!url) return [];
  try {
    const csvText = await fetchCsvText(url);
    if (!csvText.trim()) return [];
    const rows = csvParse(csvText) as CsvRow[];
    return rows.map(normaliseCountryRow).filter((row) => row.count > 0);
  } catch (error) {
    console.warn("[blulog] fetchCountries fallback:", error);
    return [];
  }
}

function normaliseArticleRow(row: CsvRow): ArticlesRow {
  const article = row["unique_ei"] ?? row["EI"] ?? row["article"] ?? "N/A";
  const count = ensureNumber(row["count_ei"] ?? row["count"] ?? row["Nombre"]);
  return {
    article,
    count: Number.isFinite(count) ? Number(count) : 0,
  };
}

export async function fetchArticles(url: string): Promise<ArticlesRow[]> {
  if (!url) return [];
  try {
    const csvText = await fetchCsvText(url);
    if (!csvText.trim()) return [];
    const rows = csvParse(csvText) as CsvRow[];
    return rows.map(normaliseArticleRow).filter((row) => row.count > 0);
  } catch (error) {
    console.warn("[blulog] fetchArticles fallback:", error);
    return [];
  }
}

function parseSamples(rows: CsvRow[]): SamplePoint[] {
  return rows
    .map((row) => {
      const date = safeParseDate(row["date"] ?? row["Date"] ?? row["timestamp"]);
      const value = ensureNumber(row["measure"] ?? row["value"] ?? row["Valeur"]);
      if (!date || !Number.isFinite(value)) {
        return null;
      }
      return { date, value: Number(value) };
    })
    .filter((item): item is SamplePoint => item !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

async function fetchTrackingCsv(url: string | null): Promise<CsvRow[]> {
  if (!url) return [];
  try {
    const csvText = await fetchCsvText(url);
    if (!csvText.trim()) return [];
    return csvParse(csvText) as CsvRow[];
  } catch (error) {
    console.warn("[blulog] fetchTrackingCsv fallback:", error);
    return [];
  }
}

export async function fetchTrackingDetails(prefix: string, trackingNumber: string, expeditionCount: number): Promise<TrackingDetails> {
  if (!trackingNumber) {
    return { sensors: [], expeditions: [], startDate: null, endDate: null };
  }

  const infoUrl = createBlulogFileUrl(prefix, `${trackingNumber}/infodata.csv`);
  const infoRows = await fetchTrackingCsv(infoUrl);
  const sensorIds = infoRows
    .map((row) => row["sensor_id"] ?? row["Sensor"] ?? row["id"])
    .filter((value): value is string => Boolean(value));

  const uniqueSensorIds = Array.from(new Set(sensorIds));

  if (!uniqueSensorIds.length) {
    console.warn("[blulog] Aucun capteur trouvé dans infodata", { trackingNumber, infoUrl, rows: infoRows.length });
  }

  const sensors: SensorSeries[] = await Promise.all(
    uniqueSensorIds.map(async (sensorId, index) => {
      const url = createBlulogFileUrl(prefix, `${trackingNumber}/${sensorId}.csv`);
      const rows = await fetchTrackingCsv(url);
      console.info("[blulog] Chargement capteur", { trackingNumber, sensorId, samples: rows.length, url });
      return {
        sensorId,
        order: index,
        samples: parseSamples(rows),
      };
    }),
  );

  const expeditions: ExpeditionSeries[] = [];
  if (Number.isFinite(expeditionCount) && expeditionCount > 0) {
    for (let expIndex = 0; expIndex < expeditionCount; expIndex += 1) {
      await Promise.all(
        uniqueSensorIds.map(async (sensorId) => {
          const url = createBlulogFileUrl(prefix, `${trackingNumber}/${expIndex}/${sensorId}.csv`);
          const rows = await fetchTrackingCsv(url);
          if (rows.length) {
            console.info("[blulog] Chargement expé", {
              trackingNumber,
              sensorId,
              expeditionIndex: expIndex,
              samples: rows.length,
              url,
            });
          }
          const samples = parseSamples(rows);
          if (samples.length) {
            expeditions.push({
              sensorId,
              expeditionIndex: expIndex,
              samples,
            });
          }
        }),
      );
    }
  }

  const allDates = [
    ...sensors.flatMap((sensor) => sensor.samples.map((sample) => sample.date)),
    ...expeditions.flatMap((serie) => serie.samples.map((sample) => sample.date)),
  ]
    .map((date) => date?.getTime())
    .filter((value): value is number => Number.isFinite(value));

  const startDate = allDates.length ? new Date(Math.min(...allDates)) : null;
  const endDate = allDates.length ? new Date(Math.max(...allDates)) : null;


  console.info("[blulog] Résumé tracking", {
    trackingNumber,
    sensors: sensors.map((serie) => ({ sensorId: serie.sensorId, points: serie.samples.length })),
    expeditions: expeditions.map((serie) => ({ sensorId: serie.sensorId, expeditionIndex: serie.expeditionIndex, points: serie.samples.length })),
    startDate,
    endDate,
  });

  return { sensors, expeditions, startDate, endDate };
}
